import ReactChart from '@/components/ReactChart/ReactChart'
import cls from 'classnames'
import styles from './CoreChart.module.less'
import * as echarts from 'echarts/core'
import { commonUtils } from '@/helpers/commonUtils'
import { useTranslateDimension } from '../../../VideoAnalysis'
import { EChartsOption } from 'echarts'
import { memo } from 'react'
/* import {
  EYE_MOVEMENT_RATIO,
  COGNITIVE_ENGAGEMENT,
  EMOTION_RECOGNITION,
} from '../../../VideoAnalysis' */
// import dayjs from 'dayjs'

interface CoreChartProps {
  detailMetrics: ProjectModels.DetailMetricItem[]
  onChartReady?: (chart: echarts.ECharts) => void
  options?: CustomChartOptions
  className?: string
  customConfig?: (config: EChartsOption) => EChartsOption
  layerMode?: 'full' | 'axis' | 'series'
}

export interface CustomChartOptions {
  height?: string
  dotSize?: number
  labelVisible?: boolean
}

const getSecond = (second: number) => {
  return second - 0.5
}

const DEFAULT_CHART_HEIGHT = 300

export const useChartConfig = (
  detailMetrics: ProjectModels.DetailMetricItem[],
  options?: CustomChartOptions,
  customConfig?: (config: EChartsOption) => EChartsOption,
  layerMode: 'full' | 'axis' | 'series' = 'full'
) => {
  const { EYE_MOVEMENT_RATIO, COGNITIVE_ENGAGEMENT, EMOTION_RECOGNITION } = useTranslateDimension()
  const values = detailMetrics.reduce(
    (prev, cur) => {
      const eyeMovementRatio = cur.eyeMovementRationScore
      const cognitiveEngagement = cur.cognitiveEngagementScore
      const emotionRecognition = cur.emotionRecognitionScore
      return [
        ...prev,
        { name: EYE_MOVEMENT_RATIO, count: eyeMovementRatio, second: getSecond(cur.secondCount) },
        {
          name: COGNITIVE_ENGAGEMENT,
          count: cognitiveEngagement,
          second: getSecond(cur.secondCount),
        },
        {
          name: EMOTION_RECOGNITION,
          count: emotionRecognition,
          second: getSecond(cur.secondCount),
        },
      ]
    },
    [] as Array<{ name: string; count: number | undefined; second: number }>
  )

  const indicator = {
    [EYE_MOVEMENT_RATIO]: {
      color: 'rgb(99, 176, 249 )',
      areaColor: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: 'rgb(187, 218, 255, .5)',
        },
        {
          offset: 1,
          color: 'rgb(187, 218, 255, .5)',
        },
      ]),
      genderIcon: `image://${commonUtils.getImagePath('projectDetailPage/echarts-legend-blue.svg')}`,
      data: values.filter((v) => v.name === EYE_MOVEMENT_RATIO).map((v) => v.count),
    },
    [COGNITIVE_ENGAGEMENT]: {
      color: 'rgba(131, 111, 232, 1)',
      areaColor: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: 'rgb(199, 187, 255, .5)',
        },
        {
          offset: 1,
          color: 'rgb(199, 187, 255, .5)',
        },
      ]),
      genderIcon: `image://${commonUtils.getImagePath('projectDetailPage/echarts-legend-purple.svg')}`,
      data: values.filter((v) => v.name === COGNITIVE_ENGAGEMENT).map((v) => v.count),
    },
    [EMOTION_RECOGNITION]: {
      color: 'rgba(246, 181, 109, 1)',
      areaColor: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
        {
          offset: 0,
          color: 'rgb(255, 234, 181, .5)',
        },
        {
          offset: 1,
          color: 'rgb(255, 234, 181, .5)',
        },
      ]),
      genderIcon: `image://${commonUtils.getImagePath('projectDetailPage/echarts-legend-yellow.svg')}`,
      data: values.filter((v) => v.name === EMOTION_RECOGNITION).map((v) => v.count),
    },
  }

  const config: EChartsOption = {
    color: [
      indicator[EYE_MOVEMENT_RATIO].color,
      indicator[COGNITIVE_ENGAGEMENT].color,
      indicator[EMOTION_RECOGNITION].color,
    ],
    xAxis: {
      type: 'category',
      // boundaryGap: false,
      data: detailMetrics.map((info) => {
        // return dayjs(info.secondCount * 1000).format('mm:ss')
        return `${info.secondCount}s`
      }),
      axisTick: {
        show: true,
      },
      axisPointer: {
        show: false,
        label: {
          show: false,
        },
      },
      splitLine: {
        show: true,
      },
    },
    yAxis: {
      max: 100,
      type: 'value',
      splitLine: {
        show: false,
      },
      axisPointer: {
        show: false,
      },
    },
    legend: {
      bottom: 5,
      data: [],
    },
    tooltip: {
      position: 'bottom',
      // position: [10, 10],
      axisPointer: {
        type: 'cross',
      },
    },
    grid: {
      /** chart 的间距 */
      top: 7,
      left: 28,
      right: 15,
      bottom: 20,
      height: (options && options.height ? options.height as unknown as number : DEFAULT_CHART_HEIGHT) * 0.9,
      // containLabel: true
    },
    series: [
      {
        name: EYE_MOVEMENT_RATIO,
        data: indicator[EYE_MOVEMENT_RATIO].data,
        type: 'line',
        smooth: true,
        lineStyle: { color: indicator[EYE_MOVEMENT_RATIO].color },
        symbol: 'circle',
        symbolSize: options?.dotSize || 7,
        showSymbol: options?.dotSize !== 0,
        areaStyle: {
          color: indicator[EYE_MOVEMENT_RATIO].areaColor,
        },
        label: {
          show: options?.labelVisible || false,
        },
      },
      {
        name: COGNITIVE_ENGAGEMENT,
        data: indicator[COGNITIVE_ENGAGEMENT].data,
        type: 'line',
        smooth: true,
        lineStyle: { color: indicator[COGNITIVE_ENGAGEMENT].color },
        symbol: 'circle',
        symbolSize: options?.dotSize || 7,
        showSymbol: options?.dotSize !== 0,
        areaStyle: {
          color: indicator[COGNITIVE_ENGAGEMENT].areaColor,
        },
        label: {
          show: options?.labelVisible || false,
        },
      },
      {
        name: EMOTION_RECOGNITION,
        data: indicator[EMOTION_RECOGNITION].data,
        type: 'line',
        smooth: true,
        lineStyle: { color: indicator[EMOTION_RECOGNITION].color },
        symbol: 'circle',
        symbolSize: options?.dotSize || 7,
        showSymbol: options?.dotSize !== 0,
        areaStyle: {
          color: indicator[EMOTION_RECOGNITION].areaColor,
        },
        label: {
          show: options?.labelVisible || false,
        },
      },

    ],
  }
  const hideAxisVisualConfig = (axis: any) => {
    if (!axis) { return axis }
    if (Array.isArray(axis)) {
      return axis.map((item) => ({
        ...item,
        axisLabel: { ...item?.axisLabel, show: false },
        axisTick: { ...item?.axisTick, show: false },
        axisLine: { ...item?.axisLine, show: false },
        splitLine: { ...item?.splitLine, show: false },
        axisPointer: { ...item?.axisPointer, show: false },
      }))
    }
    return {
      ...axis,
      axisLabel: { ...axis.axisLabel, show: false },
      axisTick: { ...axis.axisTick, show: false },
      axisLine: { ...axis.axisLine, show: false },
      splitLine: { ...axis.splitLine, show: false },
      axisPointer: { ...axis.axisPointer, show: false },
    }
  }

  let layeredConfig = config
  if (layerMode === 'axis') {
    layeredConfig = {
      ...config,
      series: [],
    }
  }
  if (layerMode === 'series') {
    layeredConfig = {
      ...config,
      xAxis: hideAxisVisualConfig(config.xAxis),
      yAxis: hideAxisVisualConfig(config.yAxis),
      legend: { ...(config.legend as any), show: false },
      tooltip: { ...(config.tooltip as any), show: false },
    }
  }

  return { indicator, config: customConfig ? customConfig(layeredConfig) : layeredConfig }
}

function CoreChart(props: CoreChartProps) {
  const { config } = useChartConfig(
    props.detailMetrics,
    props.options,
    props.customConfig,
    props.layerMode
  )

  return (
    <ReactChart
      className={cls(styles.coreChart, props.className)}
      style={{ height: (props.options?.height || DEFAULT_CHART_HEIGHT) }}
      option={config}
      notMerge={true}
      lazyUpdate={true}
      onChartReady={(chart) => {
        props.onChartReady?.(chart)
      }}
    />
  )
}

export default memo(CoreChart)
